import { cloneElement, type FC } from 'react';

import ArrowLeftIcon from '@/assets/svg/ui-elements/arrow-left.svg';

import { theme } from '../../theme';
import Button, { IconButton } from '../Button';
import Typography from '../Typography';
import {
  StyledActions,
  StyledDesktopRoot,
  StyledMobileActions,
  StyledMobileCenter,
  StyledMobileRoot,
  StyledMobileStart,
  StyledSubtitleZone,
  StyledTitleGroup,
} from './PageHeader.styled';
import type { PageHeaderProps } from './types';

const MAX_DESKTOP_ACTIONS = 4;
const MAX_MOBILE_ACTIONS = 2;

const PageHeader: FC<PageHeaderProps> = ({
  title,
  viewport = 'desktop',
  onBack,
  backLabel = 'Back',
  actions = [],
  mobileActions = [],
  steps,
  subtitle,
  ...rest
}) => {
  const backButton = onBack && (
    <IconButton
      type="button"
      size="xs"
      variant="solid"
      color="tertiary"
      aria-label={backLabel}
      onClick={onBack}
    >
      <ArrowLeftIcon aria-hidden="true" />
    </IconButton>
  );

  const heading = (
    <Typography as="h1" variant="body1" fontWeight={600} color={theme.colors.text.primary}>
      {title}
    </Typography>
  );

  if (viewport === 'mobile') {
    const visibleMobileActions = mobileActions.slice(0, MAX_MOBILE_ACTIONS);
    const hasSubtitle = Boolean(steps || subtitle);

    return (
      <StyledMobileRoot {...rest}>
        <StyledMobileStart>{backButton}</StyledMobileStart>

        <StyledMobileCenter>
          {heading}
          {hasSubtitle && (
            <StyledSubtitleZone>
              {steps && (
                <Typography variant="body3" fontWeight={600} color={theme.colors.text.secondary}>
                  {steps}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body3" fontWeight={600} color={theme.colors.text.secondary}>
                  {subtitle}
                </Typography>
              )}
            </StyledSubtitleZone>
          )}
        </StyledMobileCenter>

        <StyledMobileActions
          data-testid={visibleMobileActions.length > 0 ? 'page-header-actions' : undefined}
        >
          {visibleMobileActions.map(({ label, icon, onClick }, index) => (
            <IconButton
              key={`${index}-${label}`}
              type="button"
              size="xs"
              variant="solid"
              color="tertiary"
              aria-label={label}
              onClick={onClick}
            >
              {cloneElement(icon, { 'aria-hidden': true })}
            </IconButton>
          ))}
        </StyledMobileActions>
      </StyledMobileRoot>
    );
  }

  const visibleActions = actions.slice(0, MAX_DESKTOP_ACTIONS);

  return (
    <StyledDesktopRoot {...rest}>
      <StyledTitleGroup>
        {backButton}
        {heading}
      </StyledTitleGroup>

      {visibleActions.length > 0 && (
        <StyledActions data-testid="page-header-actions">
          {visibleActions.map(({ label, onClick, icon }, index) => (
            <Button
              key={`${index}-${label}`}
              type="button"
              size="sm"
              color="primary"
              variant={index === 0 ? 'solid' : 'outline'}
              onClick={onClick}
            >
              {icon && cloneElement(icon, { 'aria-hidden': true })}
              {label}
            </Button>
          ))}
        </StyledActions>
      )}
    </StyledDesktopRoot>
  );
};

PageHeader.displayName = 'PageHeader';

export default PageHeader;
